import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";
import { db } from "./config";

// ===== PORTFOLIO OPERATIONS =====

// Create a new portfolio
export const createPortfolio = async (userId, portfolioData) => {
  try {
    const docRef = await addDoc(collection(db, "portfolios"), {
      userId,
      name: portfolioData.name || "My Portfolio",
      description: portfolioData.description || "",
      totalValue: 0,
      totalInvested: 0,
      totalReturn: 0,
      returnPercentage: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    console.log("Portfolio created with ID:", docRef.id);
    return { success: true, portfolioId: docRef.id };
  } catch (error) {
    console.error("Error creating portfolio:", error);
    return { success: false, error: error.message };
  }
};

// Get all portfolios for a user
export const getUserPortfolios = async (userId) => {
  try {
    const q = query(
      collection(db, "portfolios"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc")
    );
    const querySnapshot = await getDocs(q);

    const portfolios = [];
    querySnapshot.forEach((doc) => {
      portfolios.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, data: portfolios };
  } catch (error) {
    console.error("Error getting portfolios:", error);
    return { success: false, error: error.message };
  }
};

// Update portfolio
export const updatePortfolio = async (portfolioId, updates) => {
  try {
    const docRef = doc(db, "portfolios", portfolioId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    console.error("Error updating portfolio:", error);
    return { success: false, error: error.message };
  }
};

// Delete portfolio
export const deletePortfolio = async (portfolioId) => {
  try {
    await deleteDoc(doc(db, "portfolios", portfolioId));
    return { success: true };
  } catch (error) {
    console.error("Error deleting portfolio:", error);
    return { success: false, error: error.message };
  }
};

// ===== INVESTMENT OPERATIONS =====

// Add a new investment
export const addInvestment = async (portfolioId, investmentData) => {
  try {
    const docRef = await addDoc(collection(db, "investments"), {
      portfolioId,
      symbol: investmentData.symbol,
      name: investmentData.name,
      type: investmentData.type || "stock",
      shares: investmentData.shares || 0,
      averagePrice: investmentData.averagePrice || 0,
      totalInvested: investmentData.totalInvested || 0,
      currentPrice: investmentData.currentPrice || 0,
      currentValue: investmentData.currentValue || 0,
      totalReturn: investmentData.totalReturn || 0,
      returnPercentage: investmentData.returnPercentage || 0,
      notes: investmentData.notes || "",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    console.log("Investment added with ID:", docRef.id);
    return { success: true, investmentId: docRef.id };
  } catch (error) {
    console.error("Error adding investment:", error);
    return { success: false, error: error.message };
  }
};

// Get all investments for a portfolio
export const getPortfolioInvestments = async (portfolioId) => {
  try {
    // Validate portfolioId
    if (!portfolioId) {
      console.warn("getPortfolioInvestments called with undefined portfolioId");
      return { success: true, data: [] };
    }

    const q = query(
      collection(db, "investments"),
      where("portfolioId", "==", portfolioId),
      orderBy("createdAt", "desc")
    );
    const querySnapshot = await getDocs(q);

    const investments = [];
    querySnapshot.forEach((doc) => {
      investments.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, data: investments };
  } catch (error) {
    console.error("Error getting investments:", error);
    return { success: false, error: error.message };
  }
};

// Update investment
export const updateInvestment = async (investmentId, updates) => {
  try {
    const docRef = doc(db, "investments", investmentId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    console.error("Error updating investment:", error);
    return { success: false, error: error.message };
  }
};

// Delete investment
export const deleteInvestment = async (investmentId, portfolioId) => {
  try {
    // Validate investmentId and portfolioId
    if (!investmentId || !portfolioId) {
      console.warn(
        "deleteInvestment called with undefined investmentId or portfolioId"
      );
      return { success: false, error: "Missing investmentId or portfolioId" };
    }

    // Delete all transactions for this investment
    const q = query(
      collection(db, "transactions"),
      where("investmentId", "==", investmentId),
      where("portfolioId", "==", portfolioId)
    );
    const querySnapshot = await getDocs(q);
    const batch = db.batch ? db.batch() : null; // For compatibility with modular SDK
    for (const docSnap of querySnapshot.docs) {
      if (batch) {
        batch.delete(docSnap.ref);
      } else {
        await deleteDoc(docSnap.ref);
      }
    }
    if (batch) await batch.commit();
    // Delete the investment itself
    await deleteDoc(doc(db, "investments", investmentId));
    return { success: true };
  } catch (error) {
    console.error("Error deleting investment and its transactions:", error);
    return { success: false, error: error.message };
  }
};

// ===== TRANSACTION OPERATIONS =====

// Add a new transaction (buy/sell) ATOMICALLY
export const addTransaction = async (portfolioId, transactionData) => {
  // Enforce investmentId for buy/sell transactions
  if (
    (transactionData.type === "buy" || transactionData.type === "sell") &&
    !transactionData.investmentId
  ) {
    return { success: false, error: "Missing investmentId for transaction." };
  }
  try {
    const transactionResult = await runTransaction(db, async (transaction) => {
      // Portfolio reference
      const portfolioRef = doc(db, "portfolios", portfolioId);
      const portfolioSnap = await transaction.get(portfolioRef);
      let currentCash = 0;
      if (portfolioSnap.exists()) {
        currentCash = portfolioSnap.data().cashAvailable || 0;
      }
      let newCash = currentCash;
      if (transactionData.type === "buy") {
        newCash =
          currentCash -
          (Number(transactionData.totalAmount) +
            Number(transactionData.fees || 0));
      } else if (transactionData.type === "sell") {
        newCash =
          currentCash +
          (Number(transactionData.totalAmount) -
            Number(transactionData.fees || 0));
      }

      // Handle date conversion with better validation
      let transactionDate;
      if (transactionData.date) {
        try {
          // If it's an ISO string, convert to Date object
          if (typeof transactionData.date === "string") {
            const parsedDate = new Date(transactionData.date);
            // Check if the date is valid
            if (!isNaN(parsedDate.getTime())) {
              transactionDate = parsedDate;
            } else {
              transactionDate = serverTimestamp();
            }
          } else if (transactionData.date instanceof Date) {
            // Check if the Date object is valid
            if (!isNaN(transactionData.date.getTime())) {
              transactionDate = transactionData.date;
            } else {
              transactionDate = serverTimestamp();
            }
          } else {
            transactionDate = serverTimestamp();
          }
        } catch (error) {
          console.warn("Invalid date provided, using serverTimestamp:", error);
          transactionDate = serverTimestamp();
        }
      } else {
        transactionDate = serverTimestamp();
      }

      // Add the transaction
      const txnRef = doc(collection(db, "transactions"));
      transaction.set(txnRef, {
        portfolioId,
        investmentId: transactionData.investmentId ?? null,
        symbol: transactionData.symbol ?? "",
        type: transactionData.type ?? "",
        shares: transactionData.shares ?? 0,
        price: transactionData.price ?? 0,
        currency: transactionData.currency || "USD",
        totalAmount: transactionData.totalAmount ?? 0,
        fees: transactionData.fees ?? 0,
        date: transactionDate,
        notes: transactionData.notes || "",
        createdAt: serverTimestamp(),
      });
      // Update cashAvailable
      transaction.update(portfolioRef, { cashAvailable: newCash });
      return txnRef.id;
    });
    return { success: true, transactionId: transactionResult };
  } catch (error) {
    console.error("Error adding transaction (atomic):", error);
    return { success: false, error: error.message };
  }
};

// Get all transactions for a portfolio
export const getPortfolioTransactions = async (portfolioId) => {
  try {
    // Validate portfolioId
    if (!portfolioId) {
      console.warn(
        "getPortfolioTransactions called with undefined portfolioId"
      );
      return { success: true, data: [] };
    }

    const q = query(
      collection(db, "transactions"),
      where("portfolioId", "==", portfolioId),
      orderBy("date", "desc")
    );
    const querySnapshot = await getDocs(q);

    const transactions = [];
    querySnapshot.forEach((doc) => {
      transactions.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, data: transactions };
  } catch (error) {
    console.error("Error getting transactions:", error);
    return { success: false, error: error.message };
  }
};

// Get all transactions for a specific investment
export const getInvestmentTransactions = async (portfolioId, investmentId) => {
  try {
    // Validate portfolioId and investmentId
    if (!portfolioId || !investmentId) {
      console.warn(
        "getInvestmentTransactions called with undefined portfolioId or investmentId"
      );
      return { success: true, data: [] };
    }

    const q = query(
      collection(db, "transactions"),
      where("portfolioId", "==", portfolioId),
      where("investmentId", "==", investmentId),
      orderBy("date", "desc")
    );
    const querySnapshot = await getDocs(q);

    const transactions = [];
    querySnapshot.forEach((doc) => {
      transactions.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, data: transactions };
  } catch (error) {
    console.error("Error getting investment transactions:", error);
    return { success: false, error: error.message };
  }
};

// Delete a transaction by ID
export const deleteTransaction = async (transactionId) => {
  try {
    await deleteDoc(doc(db, "transactions", transactionId));
    return { success: true };
  } catch (error) {
    console.error("Error deleting transaction:", error);
    return { success: false, error: error.message };
  }
};

// ===== WATCHLIST OPERATIONS =====

// Add to watchlist
export const addToWatchlist = async (userId, watchlistData) => {
  try {
    const docRef = await addDoc(collection(db, "watchlists"), {
      userId,
      symbol: watchlistData.symbol,
      name: watchlistData.name,
      type: watchlistData.type || "stock",
      currentPrice: watchlistData.currentPrice || 0,
      targetPrice: watchlistData.targetPrice || 0,
      notes: watchlistData.notes || "",
      createdAt: serverTimestamp(),
    });

    console.log("Added to watchlist with ID:", docRef.id);
    return { success: true, watchlistId: docRef.id };
  } catch (error) {
    console.error("Error adding to watchlist:", error);
    return { success: false, error: error.message };
  }
};

// Get user's watchlist
export const getUserWatchlist = async (userId) => {
  try {
    const q = query(
      collection(db, "watchlists"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc")
    );
    const querySnapshot = await getDocs(q);

    const watchlist = [];
    querySnapshot.forEach((doc) => {
      watchlist.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, data: watchlist };
  } catch (error) {
    console.error("Error getting watchlist:", error);
    return { success: false, error: error.message };
  }
};

// Remove from watchlist
export const removeFromWatchlist = async (watchlistId) => {
  try {
    await deleteDoc(doc(db, "watchlists", watchlistId));
    return { success: true };
  } catch (error) {
    console.error("Error removing from watchlist:", error);
    return { success: false, error: error.message };
  }
};

// ===== REAL-TIME LISTENERS =====

// Listen to portfolio changes
export const subscribeToPortfolio = (portfolioId, callback) => {
  const q = query(
    collection(db, "portfolios"),
    where("__name__", "==", portfolioId)
  );

  return onSnapshot(q, (querySnapshot) => {
    const portfolios = [];
    querySnapshot.forEach((doc) => {
      portfolios.push({ id: doc.id, ...doc.data() });
    });
    callback(portfolios[0] || null);
  });
};

// Listen to portfolio investments
export const subscribeToPortfolioInvestments = (portfolioId, callback) => {
  const q = query(
    collection(db, "investments"),
    where("portfolioId", "==", portfolioId),
    orderBy("createdAt", "desc")
  );

  return onSnapshot(q, (querySnapshot) => {
    const investments = [];
    querySnapshot.forEach((doc) => {
      investments.push({ id: doc.id, ...doc.data() });
    });
    callback(investments);
  });
};

// Listen to user's watchlist
export const subscribeToUserWatchlist = (userId, callback) => {
  const q = query(
    collection(db, "watchlists"),
    where("userId", "==", userId),
    orderBy("createdAt", "desc")
  );

  return onSnapshot(q, (querySnapshot) => {
    const watchlist = [];
    querySnapshot.forEach((doc) => {
      watchlist.push({ id: doc.id, ...doc.data() });
    });
    callback(watchlist);
  });
};

// Utility: Create sample transactions for testing
export const createSampleTransactions = async (portfolioId, investmentId) => {
  const sampleTxns = [
    {
      portfolioId,
      investmentId,
      symbol: "AAPL",
      type: "buy",
      shares: 10,
      price: 170.25,
      currency: "USD",
      totalAmount: 1702.5,
      fees: 2.5,
      date: new Date().toISOString(),
      notes: "Initial Apple buy.",
    },
    {
      portfolioId,
      investmentId,
      symbol: "AAPL",
      type: "buy",
      shares: 5,
      price: 175.1,
      currency: "USD",
      totalAmount: 875.5,
      fees: 2.5,
      date: new Date(Date.now() - 86400000 * 7).toISOString(), // 7 days ago
      notes: "Added more Apple.",
    },
    {
      portfolioId,
      investmentId,
      symbol: "AAPL",
      type: "sell",
      shares: 3,
      price: 180.0,
      currency: "USD",
      totalAmount: 540.0,
      fees: 2.5,
      date: new Date(Date.now() - 86400000 * 2).toISOString(), // 2 days ago
      notes: "Partial Apple sell.",
    },
  ];
  for (const txn of sampleTxns) {
    await addTransaction(portfolioId, txn);
  }
  return { success: true };
};

// Utility: Create two sample portfolios for a user
export const createTwoSamplePortfolios = async (userId) => {
  const portfolios = [
    { name: "Portfolio 1", description: "Sample portfolio 1" },
    { name: "Portfolio 2", description: "Sample portfolio 2" },
  ];
  const results = [];
  for (const p of portfolios) {
    const result = await createPortfolio(userId, p);
    results.push(result);
  }
  return results;
};

// Get cash available for a portfolio
export const getPortfolioCashAvailable = async (portfolioId) => {
  try {
    const docRef = doc(db, "portfolios", portfolioId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return {
        success: true,
        cashAvailable: docSnap.data().cashAvailable || 0,
      };
    } else {
      return { success: false, error: "Portfolio not found" };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Set cash available for a portfolio
export const setPortfolioCashAvailable = async (portfolioId, cash) => {
  try {
    const docRef = doc(db, "portfolios", portfolioId);
    await updateDoc(docRef, { cashAvailable: cash });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};
